import React, { createContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '@/stores';
import { setUser, clearUser } from '@/stores/slices/user-slice';
import { resetChatState } from '@/stores/slices/chat-slice';
import { selectUser } from '@/stores/selectors';
import { AuthResponse, SessionInfoResponse } from '@/types/auth';
import { User } from '@/types/user';
import { tokenStorage } from '@/utils/storage-keys';
import { apiRequest } from '@/utils/api-request';
import { apiRoutes } from '@/config/api-routes';
import { appConfig } from '@/config/app-config';
import { paths } from '@/config/paths';

export interface AuthContextType {
  isAuthenticated: boolean;
  initializeAuthIsLoading: boolean;
  user: User | null;
  handleOAuth: (token: string) => Promise<{ error: Error | null; data: AuthResponse | null }>;
  handleLogout: () => Promise<void>;
  onClickOAuth: () => Promise<void>;
}

export const authContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const pathname = window.location.pathname;

  // Local state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initializeAuthIsLoading, setInitializeAuthIsLoading] = useState(true);

  // Create user from session info
  const createUserFromSession = useCallback(async () => {
    const oauthToken: string = tokenStorage.get();

    const [errorSessionInfo, dataSessionInfo] = await apiRequest<SessionInfoResponse>({
      method: 'GET',
      url: apiRoutes.oAuth,
      headers: {
        Authorization: `Bearer ${oauthToken}`,
      },
    });

    if (errorSessionInfo || !dataSessionInfo) {
      throw new Error('Failed to fetch session info');
    }
    const userValues: User = {
      ...dataSessionInfo.user,
      subdomain: dataSessionInfo.subdomain ?? dataSessionInfo.user.subdomain,
    };

    dispatch(setUser(userValues));
  }, [dispatch]);

  // Handle authentication flow
  const handleAuthFlow = useCallback(
    async (newToken: string, newSession?: string) => {
      tokenStorage.set(newToken);
      if (newSession) {
        tokenStorage.set(newSession);
      }
      setIsAuthenticated(true);
      await createUserFromSession();
    },
    [createUserFromSession],
  );

  // Initialize auth from stored token
  const initializeAuth = useCallback(async () => {
    const token = tokenStorage.get();
    const isCallbackPath = pathname.includes(paths.Callback);

    if (!token || isCallbackPath) {
      setIsAuthenticated(false);
      setInitializeAuthIsLoading(false);
      return;
    }

    try {
      await createUserFromSession();
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth initialization failed:', error);
      tokenStorage.remove();
      setIsAuthenticated(false);
    }

    setInitializeAuthIsLoading(false);
  }, [pathname, createUserFromSession]);

  // Handle OAuth callback
  const handleOAuth = useCallback(
    async (token: string): Promise<{ error: Error | null; data: AuthResponse | null }> => {
      const [errorOAuth, dataOauth] = await apiRequest<AuthResponse>({
        method: 'GET',
        url: apiRoutes.oAuth,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (errorOAuth || !dataOauth) {
        return { error: errorOAuth, data: dataOauth };
      }

      try {
        await handleAuthFlow(token);
        tokenStorage.set(token);
      } catch (error) {
        tokenStorage.remove();
        setIsAuthenticated(false);
        return { error: error as Error, data: dataOauth };
      }

      return { error: errorOAuth, data: dataOauth };
    },
    [handleAuthFlow],
  );

  // Handle logout
  const handleLogout = useCallback(async () => {
    const oauthToken: string = tokenStorage.get();

    await apiRequest({
      method: 'POST',
      url: apiRoutes.logout,
      ...(oauthToken && { headers: { Authorization: `Bearer ${oauthToken}` } }),
    });

    tokenStorage.remove();
    dispatch(clearUser());
    dispatch(resetChatState());
    setIsAuthenticated(false);
    window.location.href = paths.Root;
  }, [dispatch]);

  // Click handler for OAuth button
  const onClickOAuth = useCallback(async () => {
    const redirectOrigin = encodeURIComponent(window.location.origin + window.location.pathname);
    window.location.href = `${appConfig.API_BASE_URL}${apiRoutes.oAuthLogin}?redirect_origin=${redirectOrigin}`;
  }, []);

  // Initialize auth and fetch URLs on mount
  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize context value
  const contextValue: AuthContextType = useMemo(
    () => ({
      isAuthenticated,
      initializeAuthIsLoading,
      user,
      handleOAuth,
      handleLogout,
      onClickOAuth,
    }),
    [isAuthenticated, initializeAuthIsLoading, user, handleOAuth, handleLogout, onClickOAuth],
  );

  return <authContext.Provider value={contextValue}>{children}</authContext.Provider>;
};

export default AuthProvider;
